/**
 * App-wide modal alerts, confirmations, and toasts.
 * Use instead of window.alert(), window.confirm(), and for success/info messages.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import AlertModal from '../components/AlertModal';
import Toast from '../components/Toast';

const AlertContext = createContext(null);

let toastId = 0;

export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    return {
      showAlert: (msg) => window.alert(typeof msg === 'string' ? msg : msg?.message || ''),
      showConfirm: (msg, opts) => (opts?.onConfirm && window.confirm(msg) ? opts.onConfirm() : opts?.onCancel?.()),
      showConfirmPromise: (msg) => Promise.resolve(window.confirm(msg)),
      showToast: () => {},
    };
  }
  return ctx;
}

export function AlertProvider({ children }) {
  const [modal, setModal] = useState(null);
  const [toasts, setToasts] = useState([]);

  const hideModal = useCallback(() => setModal(null), []);

  const showAlert = useCallback((message, options = {}) => {
    const { title, variant = 'info' } = typeof options === 'string' ? { title: options } : options;
    setModal({
      type: 'alert',
      title: title || (variant === 'error' ? 'Error' : variant === 'warning' ? 'Warning' : 'Notice'),
      message: typeof message === 'string' ? message : String(message),
      variant: variant || 'info',
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    const {
      title = 'Confirm',
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      onConfirm,
      onCancel,
      destructive = false,
    } = typeof options === 'string' ? { title: options } : options;
    setModal({
      type: 'confirm',
      title,
      message: typeof message === 'string' ? message : String(message),
      variant: destructive ? 'error' : 'info',
      confirmLabel,
      cancelLabel,
      onConfirm: onConfirm || (() => {}),
      onCancel: onCancel || (() => {}),
      destructive,
    });
  }, []);

  /** Promise-based confirm: resolves true if user clicks Confirm, false if Cancel. */
  const showConfirmPromise = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      showConfirm(message, {
        ...options,
        onConfirm: () => { (options.onConfirm || (() => {}))(); resolve(true); },
        onCancel: () => { (options.onCancel || (() => {}))(); resolve(false); },
      });
    });
  }, [showConfirm]);

  const showToast = useCallback((message, options = {}) => {
    const { variant = 'info', duration = 4000 } = typeof options === 'string' ? {} : options;
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message: typeof message === 'string' ? message : String(message), variant, duration }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const handleAlertClose = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const handleConfirmConfirm = useCallback(() => {
    if (modal?.type === 'confirm' && modal.onConfirm) modal.onConfirm();
    hideModal();
  }, [modal, hideModal]);

  const handleConfirmCancel = useCallback(() => {
    if (modal?.type === 'confirm' && modal.onCancel) modal.onCancel();
    hideModal();
  }, [modal, hideModal]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm, showConfirmPromise, showToast }}>
      {children}
      {modal && (
        <AlertModal
          type={modal.type}
          title={modal.title}
          message={modal.message}
          variant={modal.variant}
          confirmLabel={modal.confirmLabel}
          cancelLabel={modal.cancelLabel}
          destructive={modal.destructive}
          onClose={handleAlertClose}
          onConfirm={handleConfirmConfirm}
          onCancel={handleConfirmCancel}
        />
      )}
      <div className="toast-container" aria-live="polite">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            id={t.id}
            message={t.message}
            variant={t.variant}
            onDismiss={() => removeToast(t.id)}
          />
        ))}
      </div>
    </AlertContext.Provider>
  );
}
